import React, { useState } from "react";
import { WorkspaceMember } from "@common/types/workspace";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { useTransferPrimaryAdminMutation } from "@api/workspaceMember.api";
import { useScope } from "@state/scopeContext.lib";
import styles from "./WorkspaceMembers.module.scss";

interface Props {
    member: WorkspaceMember;
    show: boolean;
    onHide: () => void;
}

export const TransferPrimaryAdminModal: React.FC<Props> = ({
    member,
    show,
    onHide,
}) => {
    const { workspaceId } = useScope();
    const [emailInput, setEmailInput] = useState("");
    const [error, setError] = useState<string | null>(null);

    const transferMutation = useTransferPrimaryAdminMutation(workspaceId!);

    const targetEmail = member.user.email ?? "";
    const isEmailConfirmed =
        !!targetEmail &&
        emailInput.trim().toLowerCase() === targetEmail.toLowerCase();

    const handleConfirm = async () => {
        if (!isEmailConfirmed) {
            return;
        }

        setError(null);

        try {
            await transferMutation.mutateAsync(member.user.id);
            handleHide();
        } catch (e: unknown) {
            const message =
                e instanceof Error
                    ? e.message
                    : "Something went wrong. Please try again.";
            setError(message);
        }
    };

    const handleHide = () => {
        setEmailInput("");
        setError(null);
        onHide();
    };

    return (
        <PlainModal show={show} onHide={handleHide}>
            <StandardModalHeader>
                Transfer primary admin role
            </StandardModalHeader>
            <div className={styles.transferAdminModalBody}>
                <p className={styles.transferAdminWarning}>
                    You are about to transfer the primary admin role to{" "}
                    <strong>
                        {member.user.preferredName ?? member.user.email}
                    </strong>{" "}
                    ({targetEmail}).
                </p>
                <p className={styles.transferAdminDescription}>
                    After the transfer, you will become a regular admin. This
                    action cannot be undone without the new primary admin
                    transferring the role back.
                </p>
                <label
                    htmlFor="transfer-admin-email-confirm"
                    className={styles.transferAdminLabel}
                >
                    To confirm, type the email address of the new primary admin:
                </label>
                <input
                    id="transfer-admin-email-confirm"
                    type="email"
                    className={styles.transferAdminInput}
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder={targetEmail}
                    autoComplete="off"
                />
                {error && <p className={styles.transferAdminError}>{error}</p>}
                <div className={styles.transferAdminActions}>
                    <Button variant="secondary" size="md" onClick={handleHide}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        disabled={
                            !isEmailConfirmed || transferMutation.isPending
                        }
                        onClick={handleConfirm}
                    >
                        {transferMutation.isPending
                            ? "Transferring…"
                            : "Transfer primary admin"}
                    </Button>
                </div>
            </div>
        </PlainModal>
    );
};
